// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import "./style/Dashboard.css";
import Footer from './footer';
import VisitorTracker from '../components/VisitorTracker';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar
} from 'recharts';

export default function Dashboard() {
    
    const api = "https://mkbackend-production.up.railway.app"
    const [allOrders, setAllOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [visitorStats, setVisitorStats] = useState({
        total: 0,
        today: 0,
        week: 0,
        month: 0,
        active: 0,
        daily: []
    });
    const [stats, setStats] = useState({
        totalOrders: 0,
        accepted: 0,
        cancelled: 0,
        pending: 0,
        preparing: 0,
        onTheWay: 0,
        delivered: 0,
        totalAmount: 0,
        deliveredAmount: 0,
        onTheWayAmount: 0,
        pendingAmount: 0,
        preparingAmount: 0,
        acceptedAmount: 0,
        cancelledAmount: 0
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");

            // Fetch orders
            const ordersRes = await axios.get(
                `${api}/myOrders`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            // Fetch visitor stats
            const visitorRes = await axios.get(
                `${api}/stats`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (ordersRes.data.success) {
                setAllOrders(ordersRes.data.orders);
                calculateStats(ordersRes.data.orders);
            }

            if (visitorRes.data.success) {
                setVisitorStats(visitorRes.data.stats);
            }

        } catch (error) {
            console.log(error);
            setError(error.response?.data?.message || "Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (orders) => {
        let accepted = 0, cancelled = 0, pending = 0, preparing = 0, onTheWay = 0, delivered = 0;
        let totalAmount = 0, deliveredAmount = 0, onTheWayAmount = 0;
        let pendingAmount = 0, preparingAmount = 0, acceptedAmount = 0, cancelledAmount = 0;

        orders.forEach(order => {
            const orderTotal = order.totalAmount || 
                order.products?.reduce((sum, item) => 
                    sum + (item.productId?.price * item.quantity || 0), 0
                ) || 0;

            totalAmount += orderTotal;

            switch(order.status) {
                case 'accepted':
                    accepted++;
                    acceptedAmount += orderTotal;
                    break;
                case 'cancelled':
                    cancelled++;
                    cancelledAmount += orderTotal;
                    break;
                case 'pending':
                    pending++;
                    pendingAmount += orderTotal;
                    break;
                case 'preparing':
                    preparing++;
                    preparingAmount += orderTotal;
                    break;
                case 'on_the_way':
                    onTheWay++;
                    onTheWayAmount += orderTotal;
                    break;
                case 'delivered':
                    delivered++;
                    deliveredAmount += orderTotal;
                    break;
                default:
                    pending++;
                    pendingAmount += orderTotal;
            }
        });

        setStats({
            totalOrders: orders.length,
            accepted,
            cancelled,
            pending,
            preparing,
            onTheWay,
            delivered,
            totalAmount,
            deliveredAmount,
            onTheWayAmount,
            pendingAmount,
            preparingAmount,
            acceptedAmount,
            cancelledAmount
        });
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-text">Loading dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-box">
                    <span className="error-icon">😕</span>
                    <h2>Oops! Something went wrong</h2>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <VisitorTracker />
            
            <h1 className="dashboard-title">📊 Order Dashboard</h1>
            
            {/* Visitor Stats Section */}
            <div className="visitor-section">
                <h2 className="section-title">👥 Visitor Statistics</h2>
                <div className="visitor-stats-grid">
                    <div className="visitor-card total-visitors">
                        <div className="visitor-icon">👤</div>
                        <div className="visitor-content">
                            <h3>Total Visitors</h3>
                            <p className="visitor-number">{visitorStats.total}</p>
                        </div>
                    </div>

                    <div className="visitor-card today-visitors">
                        <div className="visitor-icon">📅</div>
                        <div className="visitor-content">
                            <h3>Today's Visitors</h3>
                            <p className="visitor-number">{visitorStats.today}</p>
                        </div>
                    </div>

                    <div className="visitor-card week-visitors">
                        <div className="visitor-icon">📊</div>
                        <div className="visitor-content">
                            <h3>This Week</h3>
                            <p className="visitor-number">{visitorStats.week}</p>
                        </div>
                    </div>

                    <div className="visitor-card month-visitors">
                        <div className="visitor-icon">📈</div>
                        <div className="visitor-content">
                            <h3>This Month</h3>
                            <p className="visitor-number">{visitorStats.month}</p>
                        </div>
                    </div>

                    <div className="visitor-card active-visitors">
                        <div className="visitor-icon">🟢</div>
                        <div className="visitor-content">
                            <h3>Active Now</h3>
                            <p className="visitor-number">{visitorStats.active}</p>
                        </div>
                    </div>
                </div>

                {/* Visitor Chart */}
                {visitorStats.daily.length > 0 && (
                    <div className="visitor-chart">
                        <h3>📈 Daily Visitors (Last 7 Days)</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={visitorStats.daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="_id" stroke="rgba(255,255,255,0.5)" />
                                <YAxis stroke="rgba(255,255,255,0.5)" />
                                <Tooltip 
                                    contentStyle={{ 
                                        background: 'rgba(0,0,0,0.8)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '8px'
                                    }}
                                />
                                <Legend />
                                <Line 
                                    type="monotone" 
                                    dataKey="count" 
                                    stroke="#667eea" 
                                    strokeWidth={2}
                                    dot={{ fill: '#667eea' }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* Order Stats Section */}
            <div className="stats-grid">
                <div className="stat-card total-orders">
                    <div className="stat-icon">📦</div>
                    <div className="stat-content">
                        <h3>Total Orders</h3>
                        <p className="stat-number">{stats.totalOrders}</p>
                        <p className="stat-amount">Rs: {stats.totalAmount.toFixed(2)}</p>
                    </div>
                </div>

                <div className="stat-card delivered">
                    <div className="stat-icon">✅</div>
                    <div className="stat-content">
                        <h3>Delivered</h3>
                        <p className="stat-number">{stats.delivered}</p>
                        <p className="stat-amount">Rs: {stats.deliveredAmount.toFixed(2)}</p>
                    </div>
                </div>

                <div className="stat-card on-the-way">
                    <div className="stat-icon">🚚</div>
                    <div className="stat-content">
                        <h3>On The Way</h3>
                        <p className="stat-number">{stats.onTheWay}</p>
                        <p className="stat-amount">Rs: {stats.onTheWayAmount.toFixed(2)}</p>
                    </div>
                </div>

                <div className="stat-card accepted">
                    <div className="stat-icon">✔️</div>
                    <div className="stat-content">
                        <h3>Accepted</h3>
                        <p className="stat-number">{stats.accepted}</p>
                        <p className="stat-amount">Rs: {stats.acceptedAmount.toFixed(2)}</p>
                    </div>
                </div>

                <div className="stat-card preparing">
                    <div className="stat-icon">👨‍🍳</div>
                    <div className="stat-content">
                        <h3>Preparing</h3>
                        <p className="stat-number">{stats.preparing}</p>
                        <p className="stat-amount">Rs: {stats.preparingAmount.toFixed(2)}</p>
                    </div>
                </div>

                <div className="stat-card pending">
                    <div className="stat-icon">🕒</div>
                    <div className="stat-content">
                        <h3>Pending</h3>
                        <p className="stat-number">{stats.pending}</p>
                        <p className="stat-amount">Rs: {stats.pendingAmount.toFixed(2)}</p>
                    </div>
                </div>

                <div className="stat-card cancelled">
                    <div className="stat-icon">❌</div>
                    <div className="stat-content">
                        <h3>Cancelled</h3>
                        <p className="stat-number">{stats.cancelled}</p>
                        <p className="stat-amount">Rs: {stats.cancelledAmount.toFixed(2)}</p>
                    </div>
                </div>
            </div>

            {/* Recent Orders Table */}
            <div className="recent-orders">
                <h2>📋 Recent Orders</h2>
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Customer</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allOrders.slice(0, 10).map((order, index) => (
                                <tr key={order._id || index}>
                                    <td>{index + 1}</td>
                                    <td>{order.customerName}</td>
                                    <td>
                                        <span className={`status-badge ${order.status || 'pending'}`}>
                                            {order.status || 'Pending'}
                                        </span>
                                    </td>
                                    <td>Rs: {(order.totalAmount || 
                                        order.products?.reduce((sum, item) => 
                                            sum + (item.productId?.price * item.quantity || 0), 0
                                        ) || 0).toFixed(2)}
                                    </td>
                                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Footer />
        </div>
    );
}